import { notification } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { COLUMN_WIDTHS } from "../../helpers/common/ag-grid-helper";
import EnterpriseGridCommon from "../AgGrid/Ag-Grid";

const PqrSummaryTable = ({
  isLoading,
  rows,
  handleRowClick,
  handleInputChange,
  setVisibleColumnsAggrid,
  visitbleColumnsAggrid,
}) => {
  const { t } = useTranslation();

  const onRowClicked = (params) => {
    handleRowClick(params.data);
  };

  const valueRender = (params) => {
    const text = params.value;
    if (text?.includes("-color")) {
      return (
        <div>
          <p className="red-note-text">
            <span className="red-note">
              {Number(text?.replace("-color", ""))}
            </span>
          </p>
        </div>
      );
    }
    return isNaN(text) ? "" : Number(text);
  };

  function resolveNested(obj, path) {
    return path.split(".").reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  const numberFilterConfig = {
    filter: "agMultiColumnFilter",
    filterParams: {
      maxNumConditions: 1,
      filters: [
        {
          filter: "agNumberColumnFilter",
          filterParams: {
            valueFormatter: (params) => {
              return params.value;
            },
            maxNumConditions: 1,
          },
        },
      ],
    },
  };

  const unitsCommonFilter = {
    pivot: true,
    floatingFilter: true,
    ...numberFilterConfig,
    valueGetter: (params) => {
      const value = resolveNested(params.data, params.colDef.field);
      return Number(value);
    },
    processCellForExport: (params) => {
      return params.value;
    },
    pivotComparator: (a, b) => b.localeCompare(a),
    cellRenderer: (params) => {
      const value = resolveNested(params.data, params.colDef.field);

      return valueRender({
        value: value,
      });
    },
  };

  const createInputColumnConfig = (field) => ({
    field: field,
    headerName: field.includes("Min") ? "Min" : "Max",
    key: field,
    editable: true,
    cellEditorPopup: true,
    suppressCellFlash: true,
    className: "ag-input-cell-bg-color",
    floatingFilter: true,
    pivot: true,
    cellRenderer: (params) => {
      return (
        <div
          ref={(ref) => {
            if (!ref) return;
            ref.onclick = (e) => {
              e.stopPropagation();
            };
          }}
          className="custom-input-field"
          onClick={(e) => e.stopPropagation()}
        >
          {params.value}
        </div>
      );
    },
    ...numberFilterConfig,
    valueGetter: (params) => Number(params.data[field] || 0.0),
    processCellForExport: (params) => params.value,
  });
  const SummaryTableColumnsListEnum = {
    PassName: "Pass Name",
    PreHeatInterpassTemperature: "Pre-Heat/ Interpass Temperature",
    AverageVoltage: "Average Voltage",
    AverageCurrent: "Average Current",
    AverageWireFeedSpeed: "Average Wire Feed Speed",
    AverageOscillationWidth: "Average Oscillation Width",
    AverageOscillationFrequency: "Average Oscillation Frequency",
    AverageTravelSpeed: "Average Travel Speed",
    ArcEnergy: "Arc Energy",
    AveragePulseFrequency: "Average Pulse Frequency",
    AveragePulseTime: "Average Pulse Time",
    AveragePeakCurrent: "Average Peak Current",
    AverageBaseCurrent: "Average Base Current",
  };

  const columnKeyToEnumMap = {
    preHeatMin: SummaryTableColumnsListEnum.PreHeatInterpassTemperature,
    preHeatMax: SummaryTableColumnsListEnum.PreHeatInterpassTemperature,
    voltsMin: SummaryTableColumnsListEnum.AverageVoltage,
    voltsMax: SummaryTableColumnsListEnum.AverageVoltage,
    ampsMin: SummaryTableColumnsListEnum.AverageCurrent,
    ampsMax: SummaryTableColumnsListEnum.AverageCurrent,
    minWireFeedSpeed: SummaryTableColumnsListEnum.AverageWireFeedSpeed,
    maxWireFeedSpeed: SummaryTableColumnsListEnum.AverageWireFeedSpeed,
    oscWidthMin: SummaryTableColumnsListEnum.AverageOscillationWidth,
    oscWidthMax: SummaryTableColumnsListEnum.AverageOscillationWidth,
    oscFrqMin: SummaryTableColumnsListEnum.AverageOscillationFrequency,
    oscFrqMax: SummaryTableColumnsListEnum.AverageOscillationFrequency,
    travelSpeedMin: SummaryTableColumnsListEnum.AverageTravelSpeed,
    travelSpeedMax: SummaryTableColumnsListEnum.AverageTravelSpeed,
    arcEnergyMin: SummaryTableColumnsListEnum.ArcEnergy,
    arcEnergyMax: SummaryTableColumnsListEnum.ArcEnergy,
    pulseFreqMin: SummaryTableColumnsListEnum.AveragePulseFrequency,
    pulseFreqMax: SummaryTableColumnsListEnum.AveragePulseFrequency,
    pulseTimeMin: SummaryTableColumnsListEnum.AveragePulseTime,
    pulseTimeMax: SummaryTableColumnsListEnum.AveragePulseTime,
    peakCurrentMin: SummaryTableColumnsListEnum.AveragePeakCurrent,
    peakCurrentMax: SummaryTableColumnsListEnum.AveragePeakCurrent,
    baseCurrentMin: SummaryTableColumnsListEnum.AverageBaseCurrent,
    baseCurrentMax: SummaryTableColumnsListEnum.AverageBaseCurrent,
    passName: SummaryTableColumnsListEnum.PassName,
  };

  const columnDefs = useMemo(
    () => [
      {
        field: "passName",
        headerName: t("pqrReport.passName"),
        key: "passName",
        pivot: true,
        pivotComparator: (a, b) => b.localeCompare(a),
        floatingFilter: true,
        filter: "agMultiColumnFilter",
        filterParams: {
          maxNumConditions: 1,
        },
        width: COLUMN_WIDTHS.small,
      },
      {
        headerName: t("pqrReport.preHeat"),
        key: "preHeat",
        children: [
          {
            ...createInputColumnConfig("preHeatMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.PreHeatInterpassTemperature
            ),
          },
          {
            ...createInputColumnConfig("preHeatMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.PreHeatInterpassTemperature
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgVoltage"),
        key: "avgVoltage",
        children: [
          {
            field: "voltsMin",
            headerName: "Min",
            key: "voltsMin",
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageVoltage
            ),
            ...unitsCommonFilter,
          },
          {
            field: "voltsMax",
            headerName: "Max",
            key: "voltsMax",
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageVoltage
            ),
            ...unitsCommonFilter,
          },
        ],
      },
      {
        headerName: t("pqrReport.avgCurrent"),
        key: "avgCurrent",
        children: [
          {
            field: "ampsMin",
            headerName: "Min",
            key: "ampsMin",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageCurrent
            ),
          },
          {
            field: "ampsMax",
            headerName: "Max",
            key: "ampsMax",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageCurrent
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgWireFeedSpeed"),
        key: "avgWireFeedSpeed",
        children: [
          {
            field: "minWireFeedSpeed",
            headerName: "Min",
            key: "minWireFeedSpeed",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageWireFeedSpeed
            ),
          },
          {
            field: "maxWireFeedSpeed",
            headerName: "Max",
            key: "maxWireFeedSpeed",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageWireFeedSpeed
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgOscillationWidth"),
        key: "avgOscillationWidth",
        children: [
          {
            field: "oscWidthMin",
            headerName: "Min",
            key: "oscWidthMin",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageOscillationWidth
            ),
          },
          {
            field: "oscWidthMax",
            headerName: "Max",
            key: "oscWidthMax",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageOscillationWidth
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgOscillationFrq"),
        key: "avgOscillationFrq",
        children: [
          {
            field: "oscFrqMin",
            headerName: "Min",
            key: "oscFrqMin",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageOscillationFrequency
            ),
          },
          {
            field: "oscFrqMax",
            headerName: "Max",
            key: "oscFrqMax",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageOscillationFrequency
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgTravelSpeed"),
        key: "avgTravelSpeed",
        children: [
          {
            field: "travelSpeedMin",
            headerName: "Min",
            key: "travelSpeedMin",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageTravelSpeed
            ),
          },
          {
            field: "travelSpeedMax",
            headerName: "Max",
            key: "travelSpeedMax",
            ...unitsCommonFilter,
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageTravelSpeed
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.arcEnergy"),
        key: "arcEnergy",
        children: [
          {
            ...createInputColumnConfig("arcEnergyMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.ArcEnergy
            ),
          },
          {
            ...createInputColumnConfig("arcEnergyMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.ArcEnergy
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgPulseFrequency"),
        key: "avgPulseFrequency",
        children: [
          {
            ...createInputColumnConfig("pulseFreqMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePulseFrequency
            ),
          },
          {
            ...createInputColumnConfig("pulseFreqMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePulseFrequency
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgPulseTime"),
        key: "avgPulseTime",
        children: [
          {
            ...createInputColumnConfig("pulseTimeMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePulseTime
            ),
          },
          {
            ...createInputColumnConfig("pulseTimeMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePulseTime
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgPeakCurrent"),
        key: "avgPeakCurrent",
        children: [
          {
            ...createInputColumnConfig("peakCurrentMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePeakCurrent
            ),
          },
          {
            ...createInputColumnConfig("peakCurrentMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AveragePeakCurrent
            ),
          },
        ],
      },
      {
        headerName: t("pqrReport.avgBaseCurrent"),
        key: "avgBaseCurrent",
        children: [
          {
            ...createInputColumnConfig("baseCurrentMin"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageBaseCurrent
            ),
          },
          {
            ...createInputColumnConfig("baseCurrentMax"),
            hide: !visitbleColumnsAggrid.includes(
              SummaryTableColumnsListEnum.AverageBaseCurrent
            ),
          },
        ],
      },
    ],
    [visitbleColumnsAggrid]
  );

  const [allColumns, setAllColumns] = useState([]);

  useEffect(() => {
    setAllColumns(
      columnDefs
        ?.map((col) => {
          if (col?.children) {
            return col?.children?.map((child) => {
              return child?.hide ? null : child.field;
            });
          }
          return col?.hide ? null : col?.field;
        })
        ?.flatMap((ite) => ite)
        .filter((ite) => ite)
    );
  }, [visitbleColumnsAggrid, columnDefs]);

  const onColumnsVisibleChanged = (params) => {
    const columnsChanged = params?.columns?.map((col) => col.colId);
    let tempColumns = [...allColumns];

    if (params.visible) {
      // User is showing columns
      columnsChanged?.forEach((col) => {
        if (!tempColumns.includes(col)) {
          tempColumns.push(col);
        }
      });
    } else {
      // User is hiding columns
      columnsChanged?.forEach((col) => {
        if (tempColumns.includes(col)) {
          tempColumns = tempColumns?.filter((ite) => ite !== col);
        }
      });
    }

    // If no columns are selected, show warning but allow it
    if (tempColumns.length === 0) {
      notification.warning({
        message: "No columns selected.",
        description:
          "You have hidden all columns. At least Pass Name should remain visible for proper functionality.",
        duration: 3,
      });
    }

    // Always ensure passName is available in the mapping
    const finalColumns = tempColumns.length === 0 ? ["passName"] : tempColumns;

    const backendSelectedColumns = [
      ...new Set(
        finalColumns?.map((tempCol) => {
          return columnKeyToEnumMap[tempCol];
        })
      ),
    ].filter(Boolean);

    setVisibleColumnsAggrid(backendSelectedColumns);
    setAllColumns(tempColumns);
  };

  return (
    <div className="ag-theme-alpine" style={{ width: "100%" }}>
      <EnterpriseGridCommon
        onColumnsVisibleChanged={onColumnsVisibleChanged}
        isLoading={isLoading}
        rows={rows || []}
        columnDefs={columnDefs}
        onRowClicked={onRowClicked}
        onCellValueChanged={(params) => {
          if (handleInputChange) {
            handleInputChange(
              params.colDef.field,
              params.newValue,
              params.data.passName
            );
          }
        }}
        exportFileName="PqrSummary-View"
        heightValue={-80}
      />
    </div>
  );
};

export default PqrSummaryTable;
